import { isFunction } from 'lodash-es';
import { useMemo } from 'react';
import {
  newMathFormat,
  formatPercentNum,
  showTimeByDateType,
} from '@/pages/intelligence/utils/commonUtils';
import { formatEnNumber } from '@/pages/intelligence/pages/MarketProfile/_util';
import { getIcon } from '@/pages/intelligence/utils/echartsUtils';
import svgAppannie from '@/pages/intelligence/assets/svg/dataSource/appnnie.svg';
import svgSensorTower from '@/pages/intelligence/assets/svg/dataSource/sensor-tower.svg';
import GameDefault from '@/pages/intelligence/assets/imgs/common/game-default.svg';
import {
  toFixed0,
  getDeviceName,
} from '@/pages/intelligence/components/Chart/_config/topchartsChartUtils';
import { getCountryFlag } from '@/pages/home/_config/countries';
import { getDataSourceIcon } from '@/pages/intelligence/utils/getDataSourceImg';
import { ChartType } from '@/pages/intelligence/pages/TopCharts/_config/enums';
import {
  PLACEHOLDER,
  DATA_SOURCE_NAME_MAP,
  PC_CONSOLE_COMBINE_ENTITY_TYPE,
} from '@/pages/intelligence/pages/TopCharts/_config/topChartsConst';
import { RenderType } from '@/pages/intelligence/pages/_config/topchartsInterface';
import { imgInvertChartBubble } from '@/pages/intelligence/pages/SourcesMetrics/Sources/component/ChannelLabel';
import { renderTooltipImage } from './utils';

import './index.scss';

export const TOOLTIP_TYPES = {
  // 通用折线图Tooltip
  LINE: 'line_tooltip',
  // 通用柱状图Tooltip
  BAR: 'bar_tooltip',
  // 通用饼图Tooltip
  PIE: 'pie_tooltip',
};

export const TOP_CHARTS_TYPES = {
  // store rank下钻页 折线图
  STORE_RANK_MORE_LINE: 'store_rank_more_line',
  // store rank下钻页 柱状图
  STORE_RANK_MORE_BAR: 'store_rank_more_bar',
  // store rank详情页
  STORE_RANK_DETAILS: 'store_rank_details',
  // 公司Revenue榜单页
  TOP_COMPANIES_REVENUE: 'top_companies_revenue',
  // 公司Downloads榜单页
  TOP_COMPANIES_DOWNLOADS: 'top_companies_downloads',
  // 公司Mentions榜单页
  TOP_COMPANIES_MENTIONS: 'top_companies_mentions',
  // mobile compare页 Active User、Revenue、Downloads页签
  COMPARE_MOBILE_ACTIVEREVDOWN: 'compare-mobile-activerevdown',
  // pcConsole compare页 折线图
  COMPARE_PC_CONSOLE_LINE: 'compare-pc-console-line',
  // 直播influencer页 折线图
  STREAMSTRING_INFLUENCER: 'streaming_influencer',
  // Genre Profile 榜单页 区块Tree图
  GENRE_PROFILE_TOP_TREE: 'genre_profile_top_tree',
  // Genre Profile 榜单页 区块Tree图
  GENRE_PROFILE_INSIGHTS_LINE: 'genre_profile_insights_line',
  // Genre Profile 详情页Stats页签 折线图
  GENRE_PROFILE_DETAILS_STATS_LINE: 'genre_profile_details_stats_line',
};

// 折线图hover浮窗 需要按照指标数值降序排序的 topChartsType
const SORTABLE_LINE_TOPCHARTSTYPES = [
  TOP_CHARTS_TYPES.STORE_RANK_MORE_LINE,
  TOP_CHARTS_TYPES.COMPARE_MOBILE_ACTIVEREVDOWN,
  TOP_CHARTS_TYPES.COMPARE_PC_CONSOLE_LINE,
  TOP_CHARTS_TYPES.STREAMSTRING_INFLUENCER,
  TOP_CHARTS_TYPES.GENRE_PROFILE_DETAILS_STATS_LINE,
  'combine-stats-line',
  'pcconsole-stats-line-country',
  'pcconsole-country-bar',
  'pcconsole-country-ampere-bar',
  'pcMetricsTable',
  'stats-activeuser-line',
  'genreProfile-index',
  'rpd',
];

// 折线图hover浮窗 需要排序的topChartsType 对应的排序方式
const SORT_TYPES_LINE_TOPCHARTSTYPES: { [key: string]: 'asc' | 'desc' } = {
  [TOP_CHARTS_TYPES.STORE_RANK_MORE_LINE]: 'asc',
  [TOP_CHARTS_TYPES.COMPARE_MOBILE_ACTIVEREVDOWN]: 'desc',
  [TOP_CHARTS_TYPES.COMPARE_PC_CONSOLE_LINE]: 'desc',
  [TOP_CHARTS_TYPES.STREAMSTRING_INFLUENCER]: 'asc',
  [TOP_CHARTS_TYPES.GENRE_PROFILE_DETAILS_STATS_LINE]: 'desc',
};

interface IChartBubbleProps {
  // 头部左侧icon的url
  headerLeftIcon?: string;
  ifNoBarImg?: boolean;
  ifNeedSourceImg?: boolean;
  ifHasOtherHeadContent?: boolean;
  // 头部左侧icon的class类名
  headerLeftIconClassName?: string;
  barListValue?: string;
  // 头部左侧title
  headerLeftTitle?: string;
  isCenter?: boolean;
  isStackBar?: boolean;
  isCN?: boolean;
  centerList?: any;
  visuailsMetricValues?: any;
  // 头部左侧title的formatter
  headerLeftTitleFormatter?: (name: string) => string;
  // 头部右侧title
  headerRightTitle?: string;
  // 是否强制开启serieNameFormatter，忽略topChartsType的判定逻辑，默认关闭
  useSerieNameFormatter?: boolean;
  // serie的name formatter
  serieNameFormatter?: (name: string) => string;
  // value的formatter
  valueFormatter?: (value: any) => any;
  shouldMoveTooltipOthersLast?: boolean;
  tooltipOthersNames?: string[];
  [key: string]: any;
  topTitle?: string; // ~~~关键参数，主标题内容，传入会优先展示
  secondTitleLeft?: string; // ~~~关键参数，次标题左侧内容，传入会优先展示
  secondTitleRight?: string; // ~~~关键参数，次标题左侧内容，传入会优先展示
  otherTitleLeft?: string; // ~~~关键参数，其他标题左侧内容，传入会优先展示
  otherTitleRight?: string; // ~~~关键参数，其他标题左侧内容，传入会优先展示
  lineSourceIcon?: string; // ~~~关键参数，折线图数据源icon，传入会优先展示
  lineSourceName?: string; // ~~~关键参数，折线图数据源名称，传入会优先展示
}

const ChartBubble = (porps: IChartBubbleProps) => {
  const {
    data,
    titleNow,
    topChartsType,
    imageType = 'mobile',
    chartType = ChartType.Bar,
    headerLeftTitle,
    headerLeftTitleFormatter,
    headerRightTitle,
    useSerieNameFormatter = false,
    serieNameFormatter,
    valueFormatter,
    platform,
    dateType, // 如果有传时间粒度，则为需要转化日期格式的，否则为原字符串时间
    showXLabel,
    leftContent,
    rightContent,
    isPercent = false,
    isPercentageDecimal = false,
    tooltipType = '', // 浮窗类型
    startDate = '',
    endDate = '',
    timeText = '', // 替换掉左侧的特殊时间字符串
    // metricText = '',  //右侧metric字符串值
    barListValue = '',
    ifHasOtherHeadContent = false,
    isCenter = false,
    centerList,
    isStackBar = false,
    visuailsMetricValues = [],
    ifNeedSourceImg = true,
    headerLeftIconClassName,
    isCN = false,

    topTitle, // ~~~关键参数，主标题内容，传入会优先展示
    secondTitleLeft, // ~~~关键参数，次标题左侧内容，传入会优先展示
    secondTitleRight, // ~~~关键参数，次标题左侧内容，传入会优先展示
    otherTitleLeft, // ~~~关键参数，其他标题左侧内容，传入会优先展示
    otherTitleRight, // ~~~关键参数，其他标题左侧内容，传入会优先展示
    lineSourceIcon, // ~~~关键参数，折线图数据源icon，传入会优先展示
    lineSourceName, // ~~~关键参数，折线图数据源名称，传入会优先展示
    dataSourceEachers = ['data.ai'],
    data_source,
    dataSource,
    shouldMoveTooltipOthersLast = false,
    tooltipOthersNames = [],
  } = porps;

  // console.debug('bubble', porps);

  // 用三于种类型的判断，主要为了兼容默认line,bar,pie类型可视化参数
  // BAR_TOOLTIP或者没传tooltipType，柱状图默认有头部内容，若不需要头部内容，tooltipType传其他值

  const isBar = () => {
    return tooltipType == TOOLTIP_TYPES.BAR || (tooltipType == '' && chartType == ChartType.Bar);
  };
  const isLine = () => {
    return (
      tooltipType == TOOLTIP_TYPES.LINE ||
      (tooltipType == '' && chartType == ChartType.Line) ||
      chartType == 'stackedLine' ||
      chartType == 'discount-chart'
    );
  };
  const isPie = () => {
    return tooltipType == TOOLTIP_TYPES.PIE || (tooltipType == '' && chartType == ChartType.Pie);
  };

  const getSeriesNameTemp = (item: any) => {
    let seriesNameTemp = item?.seriesName?.trim();
    if (!seriesNameTemp) return '-';
    if (typeof serieNameFormatter === 'function') {
      seriesNameTemp = serieNameFormatter(seriesNameTemp);
    }
    seriesNameTemp = seriesNameTemp?.trim() ? seriesNameTemp : '-';
    return `<div class="bubble-lf-name">${seriesNameTemp}</div>`;
  };

  const getParamField = (payload: any, field: string) => {
    if (payload?.data && Object.prototype.hasOwnProperty.call(payload.data, field)) {
      return payload.data[field];
    }
    return payload?.[field];
  };

  const normalizeTooltipName = (name: any) => {
    if (typeof name !== 'string') {
      return '';
    }

    return name.trim().toLowerCase();
  };

  const getIsOthersTooltipItem = (item: any) => {
    const othersNameSet = new Set(
      ['others', ...tooltipOthersNames].map(normalizeTooltipName).filter(Boolean),
    );
    const names = [
      item?.seriesName,
      item?.name,
      item?.data?.seriesName,
      item?.data?.name,
      item?.data?.market,
      item?.data?.cover,
    ];

    return names.some((name) => {
      return othersNameSet.has(normalizeTooltipName(name));
    });
  };

  const moveTooltipOthersLast = (list: any[]) => {
    if (!shouldMoveTooltipOthersLast || !Array.isArray(list)) {
      return list;
    }

    const normalList: any[] = [];
    const othersList: any[] = [];
    list.forEach((item) => {
      if (getIsOthersTooltipItem(item)) {
        othersList.push(item);
        return;
      }

      normalList.push(item);
    });

    return [...normalList, ...othersList];
  };

  // 获取副头部左侧时间内容
  const getLeftTimeText = (data: any) => {
    if (isLine()) {
      return getTitleValue(data);
    }
    if (timeText && timeText != '') {
      const tempStr =
        Array.isArray(data) && data?.length > 0 && data[0].axisValue ? data[0].axisValue : '';
      const latestTimeStr =
        Array.isArray(data) && data?.length > 0 && data[0]?.data?.latest_time
          ? data[0]?.data?.latest_time
          : '';
      switch (topChartsType) {
        case 'steamSales—players_by_country':
          return `${timeText} ${dateType ? showTimeByDateType(tempStr, dateType?.toLocaleLowerCase()) : tempStr
            }`;
        case 'steamSales—players_by_country-topcharts':
          return `${timeText} ${latestTimeStr}`;
        case 'steamSales—countries-players_by_country':
          const dataTemp =
            Array.isArray(data) && data?.length > 0 && data[0].data ? data[0].data : {};
          const strTemp = dataTemp.date || '';
          return strTemp;
        default:
          return timeText;
      }
    }
    if (startDate != '' && endDate != '') return `${startDate}~${endDate}`;
    return '';
  };

  // 根据外部传入的两种数据源类型，获取数据源对应值
  const srouceValueCur = () => {
    if (dataSource) return dataSource;
    if (Array.isArray(data_source) && data_source?.length > 0) return data_source[0];
    return 'appannie';
  };

  const getListLeftImg = (item: any) => {
    switch (topChartsType) {
      case 'activeuser':
      case 'databrain':
        return `<div class="bubble-lf-imgbox">
                  ${renderTooltipImage(getIcon(item?.seriesName), 'bubble-lf-img')}
                </div>`;
      case 'stats-activeuser':
        return `<div class="bubble-lf-imgbox">
                  ${renderTooltipImage(getIcon(platform), 'bubble-lf-img')}
                </div>`;
      case 'rpd':
        return imageType == 'mobile'
          ? `<div class="bubble-lf-imgbox">
                    ${renderTooltipImage(
            srouceValueCur() === 'appannie' ? svgAppannie : svgSensorTower,
            'bubble-lf-img',
          )}
              </div>`
          : '';
      default:
        return '';
    }
  };

  const getTitleImg = (data: any) => {
    // console.log('data444', data);
    let img = '';
    if (Array.isArray(data) && data?.length > 0 && imageType != 'region') {
      img = data[0]?.data?.cover || data[0]?.data?.url || data[0]?.data?.market || '';
      if (img != '') {
        switch (topChartsType) {
          case 'streaming_influencer':
          case 'global-view-genre-market':
          case 'steamSales—countries-players_by_country':
            return '';
          case 'stats-countries-bar':
          case 'stats-rpdedition-bar-country':
          case 'pcconsole-country-bar':
          case 'pcconsole-country-bar-pcu':
          case 'pcconsole-country-ampere-bar':
          case 'steamSales—countries-players_by_country':
            return renderTooltipImage(
              getCountryFlag(
              (img == 'Others' ? '' : img)?.toLocaleUpperCase(),
              ),
              getImgFlexSizeClass('country'),
            );
          case 'newrelease':
          case 'upcoming':
          case 'rpd':
          case 'retention':
          case 'stats-activeuser-bar':
          default:
            return renderTooltipImage(img, getImgFlexSizeClass(imageType));
        }
      } else {
        return '';
      }
    } else {
      return '';
    }
  };

  const getListLeftGameImg = (data: any) => {
    if (data?.data && data?.data?.cover && data?.data?.cover != '') {
      switch (topChartsType) {
        case 'top_demographics':
        case 'newrelease':
        case 'following-common-company':
        case 'console-japanSales-line':
        case 'stats-retentionmonthly-trend':
        case 'stats-rpd-line':
        case 'genreProfile-index':
        case 'pcconsole-stats-line':
        case 'combine-stats-line':
        case 'details-streaming':
        case 'pcconsole-stats-line-country':
        case 'detail-demographics':
        case 'streaming_influencer':
          return '';
        // case 'stats-countries-line':
        // case 'stats-retentionmonthly-line':
        //   return `<div class="${getLeftGameImgboxClass(imageType)}" class="bubble-lf-gameimgbox">
        //   <img src="${getCountryFlag(data?.data?.cover?.toLocaleLowerCase())}" class="${getImgFlexSizeClass(imageType)}" />
        // </div>`;
        default:
          return `<div class="${getLeftGameImgboxClass(imageType)}" class="bubble-lf-gameimgbox">
          ${renderTooltipImage(
            imageType == 'market'
              ? getCountryFlag(data?.data?.cover?.toLocaleLowerCase())
              : data?.data?.cover || GameDefault,
            getImgFlexSizeClass(imageType),
          )}
        </div>`;
      }
    } else {
      return '';
    }
  };

  const getTitleValue = (data: any) => {
    // console.log('data22222', data);
    const tempStr =
      Array.isArray(data) && data?.length > 0 && data[0].axisValue ? data[0].axisValue : '';
    switch (topChartsType) {
      case 'rpd':
        return Array.isArray(data) ? data[0].axisValueLabel : '';
      case TOP_CHARTS_TYPES.STORE_RANK_MORE_BAR:
      case TOP_CHARTS_TYPES.STORE_RANK_DETAILS:
      // case TOP_CHARTS_TYPES.TOP_COMPANIES_REVENUE:
      // case TOP_CHARTS_TYPES.TOP_COMPANIES_DOWNLOADS:
      // case TOP_CHARTS_TYPES.TOP_COMPANIES_MENTIONS:
      case TOP_CHARTS_TYPES.GENRE_PROFILE_INSIGHTS_LINE:
      case 'top_demographics':
        return headerLeftTitle || '';
      case TOP_CHARTS_TYPES.COMPARE_PC_CONSOLE_LINE:
        return headerLeftTitleFormatter?.(tempStr) || tempStr || '-';
      // case 'stats-retentionmonthly-line':
      //   return (Array.isArray(data) && data?.length > 0 && data[0]?.axisValue) ? data[0]?.axisValue : '';
      case 'streaming-channel':
        return titleNow || '';
      case 'steamSales—players_by_country':
        return '';
      // case 'steamSales—players_by_country-topcharts':
      //   return `As of ${latestTimeStr}`;
      // case 'steamSales—countries-players_by_country':
      //   const dataTemp = (Array.isArray(data) && data?.length > 0 && data[0].data) ? data[0].data : {};
      //   const strTemp = dataTemp.date || '';
      //   return strTemp;
      case 'overview-card':
        return showXLabel || '';
      case 'marketProfile-map':
        return data.name || '';
      case 'marketProfile-annual-bar':
        return data[0].axisValueLabel || '';
      case 'marketProfile-mapTree':
        return headerLeftTitle || '';
      case 'marketProfile-insight-bar':
        return headerLeftTitle || '';
      case 'stats-countries-bar':
      case 'stats-rpdedition-bar':
      case 'stats-rpdedition-bar-country':
      case 'pcconsole-country-bar':
      case 'pcconsole-country-ampere-bar':
        const translatedStrTmp = serieNameFormatter?.(tempStr) || tempStr;
        return dateType
          ? showTimeByDateType(translatedStrTmp, dateType?.toLocaleLowerCase())
          : translatedStrTmp;
      case 'pcconsole-country-bar-pcu':
        return Array.isArray(data) && data?.length > 0 && data[0].data ? data[0].data?.name : '';
      case 'marketProfile-overview-sum':
        return showXLabel;
      case 'stats-retentionmonthly-trend':
        return tempStr || '';
      case 'steamSales—countries-players_by_country':
        `<div class="dbi-chart-bubble-title"><div>${startDate}~${endDate}</div><div>Share</div></div>`;
        return '';
      default:
        return dateType
          ? `${showTimeByDateType(tempStr, dateType?.toLocaleLowerCase())}${['hourly', 'hour'].includes(dateType.toLowerCase()) ? ' UTC+8' : ''}`
          : tempStr;
    }
  };

  const getRightTitle = (data: any) => {
    // console.log('data2222233312111', data);
    switch (topChartsType) {
      // case 'pcconsole-country-bar':
      // case 'pcconsole-stats-line-pcu': //allMetrics有标题
      // case 'pcconsole-country-bar-pcu':
      case 'steamSales—countries-players_by_country':
        return 'Share';
      case 'streaming-channel':
        return Array.isArray(data) && data?.length > 0 && data[0]?.axisValue
          ? data[0]?.axisValue
          : '';
      case 'stats-activeuser':
      case 'stats-rpd-line':
      case 'pcconsole-stats-line':
        return Array.isArray(data) && data?.length > 0 && data[0]?.seriesName
          ? data[0]?.seriesName
          : '';
      // marketProfile-overview-sum 使用默认传入的 headerRightTitle
      // case 'marketProfile-overview-sum':
      // return Array.isArray(data) && data?.length > 0 && data[0]?.name ? data[0]?.name : '';
      default:
        return headerRightTitle || titleNow || '';
    }
  };

  // 不需要数组列表内容的可视化浮窗类型
  const ifHasListContent = () => {
    switch (topChartsType) {
      case 'marketProfile-map':
      case 'marketProfile-mapTree':
      case 'genre_profile_top_tree':
        return false;
      default:
        return true;
    }
  };

  const getListName = (item: any) => {
    // console.log('itemitem22222', item);
    // return `<div class="bubble-lf-name">${item?.data?.name ?? item?.seriesName ?? PLACEHOLDER}</div>`;
    switch (topChartsType) {
      case 'activeuser':
      case 'revenue':
      case 'downloads':
        return item?.seriesName;
      case 'databrain':
        return item?.seriesName?.replace('databrain', '');
      case 'global-view-genre-market':
        const name = item?.data?.name;
        const nameCN = item?.data?.nameCN;
        let seriesName = (isCN ? nameCN : name) || name;
        seriesName = serieNameFormatter?.(seriesName) ?? seriesName;
        return `<span class="dbi-tooltip-body-item-left-name">${seriesName || '-'}</span>`;
      case 'rpd':
        return dataSourceEachers[0];
      case 'steamSales—players_by_country':
        return item?.data?.name ?? item?.seriesName ?? PLACEHOLDER;
      case 'steamSales—players_by_country-topcharts':
        return `<div class="bubble-lf-name">${item?.data?.name ?? PLACEHOLDER}</div>`;
      case 'pcconsole-country-bar':
      case 'pcconsole-country-ampere-bar':
        return imageType == 'combine' && isStackBar ? getDeviceName(item?.seriesName || '') : '';
      // return getDeviceName(item?.seriesName || '');
      // case 'pcconsole-country-bar-pcu':
      //   return item?.axisValue || '';
      case TOP_CHARTS_TYPES.COMPARE_PC_CONSOLE_LINE:
      case TOP_CHARTS_TYPES.STORE_RANK_MORE_BAR:
      case TOP_CHARTS_TYPES.STORE_RANK_DETAILS:
        return getSeriesNameTemp(item);
      case 'steamSales—countries-players_by_country':
        return `<div class="bubble-lf-name">${item?.data?.name ?? PLACEHOLDER}</div>`;
      default: {
        // MetricsTable 柱状图浮窗：优先展示当前指标名，避免只显示数据源（例如 "Steam"）
        if (topChartsType === 'pcMetricsTable') {
          if (barListValue) return barListValue;
          if (titleNow) return titleNow;
        }

        const dataSourceName = getParamField(item, 'dataSourceName');
        if (dataSourceName) return dataSourceName;
        return barListValue != '' ? barListValue : '';
      }
    }
  };

  const getListValue = (item: any) => {
    let isPerCentItem = isPercent;
    if (
      visuailsMetricValues &&
      Array.isArray(visuailsMetricValues) &&
      visuailsMetricValues?.length > 0
    ) {
      const rangType = visuailsMetricValues?.find(
        (subItem: any) => subItem.text == item.name,
      )?.renderType;
      isPerCentItem = rangType === RenderType.GROWTH_ONLY;
    }
    if (typeof valueFormatter === 'function') {
      return valueFormatter(item);
    }
    // 折线图通用获取数据
    if (isPie())
      return (
        newMathFormat(Number(item?.value) || '-') + (item?.percent ? `(${item?.percent}%)` : '')
      );
    const lineData =
      Array.isArray(item?.value) &&
        item?.value?.filter((value) => Boolean(value) || value === 0)?.length > 1
        ? newMathFormat(item.value[1]) + (isPerCentItem ? '%' : '')
        : '-';
    // 通用柱状图获取数据
    const barData =
      item?.value && !Array.isArray(item?.value)
        ? newMathFormat(isPercentageDecimal ? item?.value * 100 : item?.value) +
        (isPerCentItem ? '%' : '')
        : '-';
    switch (topChartsType) {
      case 'stats-activeuser':
        return barData;
      case 'rpd':
      case 'pcMetricsTable':
        return chartType == 'bar' ? newMathFormat(item?.value, true) : lineData;
      case 'steamSales—players_by_country':
      case 'steamSales—players_by_country-topcharts':
      case 'steamSales—countries-players_by_country':
        return item?.value ? `${newMathFormat(item?.value)}%` : '-';
      case 'retention':
      case 'stats-retentionmonthly-trend':
      case 'stats-retentionmonthly-line': {
        const isMauMetric = titleNow?.toLowerCase() === 'mau';
        const formatRetentionValue = (rawValue: any) => {
          if (rawValue === '' || rawValue === '-' || rawValue == null) {
            return '-';
          }
          return isMauMetric ? newMathFormat(rawValue, true) : toFixed0(rawValue);
        };
        if (Array.isArray(item?.value) && item?.value?.length > 1) {
          return formatRetentionValue(item.value[1]);
        }
        // Following 双源留存（VisualisAnnieSensortower + useActiveUserLineCharts）为标量值
        if (
          item?.value !== '' &&
          item?.value !== '-' &&
          item?.value != null &&
          !Array.isArray(item?.value)
        ) {
          return formatRetentionValue(item.value);
        }
        return '-';
      }
      case TOP_CHARTS_TYPES.GENRE_PROFILE_INSIGHTS_LINE:
        const { componentSubType = 'bar', value } = item || {};
        return componentSubType === 'bar' ? newMathFormat(value) : formatPercentNum(value);
      // store rank下钻页柱状图
      case TOP_CHARTS_TYPES.STORE_RANK_MORE_BAR: {
        const serieKey = item?.seriesName;
        const serieValue = item?.value?.[serieKey];
        return serieValue ? newMathFormat(serieValue) : '-';
      }
      // store rank详情页
      case TOP_CHARTS_TYPES.STORE_RANK_DETAILS: {
        const serieValue = item?.value || item?.data?.value;
        return serieValue ? newMathFormat(serieValue) : '-';
      }
      case TOP_CHARTS_TYPES.STORE_RANK_MORE_LINE:
        return item?.value || '-';
      case 'metrictable-steam-price':
        return item?.value ?? '-';
      default:
        return Array.isArray(item?.value) ? lineData : barData;
    }
  };

  const getImgFlexSizeClass = (type: string) => {
    switch (type) {
      case 'mobile':
      case 'company':
        return 'bubble-title-flex-img-mobile';
      case 'pc':
        return 'bubble-title-flex-img-pc';
      case 'console':
      case PC_CONSOLE_COMBINE_ENTITY_TYPE:
        return 'bubble-title-flex-img-console';
      case 'compare':
        return 'bubble-title-flex-img-compare';
      case 'country':
      case 'region':
      case 'market':
        return 'bubble-title-flex-img-country';
      default:
        return 'bubble-title-flex-img';
    }
  };

  const getLeftGameImgboxClass = (type: string) => {
    switch (type) {
      case 'mobile':
        return 'bubble-lf-gameimgbox-mobile';
      case 'pc':
        return 'bubble-lf-gameimgbox-pc';
      case 'console':
      case PC_CONSOLE_COMBINE_ENTITY_TYPE:
        return 'bubble-lf-gameimgbox-console';
      case 'compare':
        return 'bubble-lf-gameimgbox-compare';
      case 'country':
      case 'region':
      case 'market':
        return 'bubble-lf-gameimgbox-country';
      default:
        return 'bubble-lf-gameimgbox-mobile';
    }
  };

  const formatDataSourceSerieName = (name: string): string =>
    name?.replace('Annie_', `${DATA_SOURCE_NAME_MAP.appannie}_`);

  const getListLeftText = (item: any) => {
    // console.log('topChartsType',topChartsType);
    // console.log('itemitem',item);
    if (useSerieNameFormatter && isFunction(serieNameFormatter)) {
      return `<div class="bubble-lf-name">${serieNameFormatter?.(item?.seriesName)}</div>`;
    }
    switch (topChartsType) {
      case TOP_CHARTS_TYPES.COMPARE_PC_CONSOLE_LINE:
      case TOP_CHARTS_TYPES.STORE_RANK_DETAILS:
        return getSeriesNameTemp(item);
      default:
        return `<div class="bubble-lf-name">${formatDataSourceSerieName(item.seriesName)}</div>`;
    }
  };

  const resolveLineSourceIcon = (
    iconStr: string,
    preferDataSourceIcon = false,
  ) => {
    // 页面级 icon 可能是完整图片地址；行内 dataSourceKey 仍需走统一数据源映射。
    if (
      topChartsType === TOP_CHARTS_TYPES.STORE_RANK_DETAILS &&
      !preferDataSourceIcon
    ) {
      return iconStr;
    }
    return preferDataSourceIcon
      ? getDataSourceIcon(iconStr) || getIcon(iconStr)
      : getIcon(iconStr) || getDataSourceIcon(iconStr);
  };

  const getLineSourceIcon = (
    iconStr: string,
    preferDataSourceIcon = false,
  ) => {
    const icon = resolveLineSourceIcon(iconStr, preferDataSourceIcon);
    if (!icon) return '';
    switch (topChartsType) {
      case TOP_CHARTS_TYPES.STORE_RANK_DETAILS:
        return renderTooltipImage(icon, headerLeftIconClassName);
      default:
        return ` <div class="bubble-lf-imgbox">
                  ${renderTooltipImage(icon, 'bubble-lf-img')}
                </div>`;
    }
  };

  const getLineSourceName = (nameStr: string) => {
    return `<div class="bubble-lf-name ${nameStr == ' ' ? 'none-content' : ''
      }">${formatDataSourceSerieName(nameStr)}</div>`;
  };

  // line 与 scatter 真数统一通过点位元数据识别，不依赖具体数据源。
  const isTrueDataItem = (item: any) => Boolean(item?.data?.__isTrueData);

  const getListLeftLineContent = (item: any) => {
    const inlineDataSourceKey = getParamField(item, 'dataSourceKey');
    const inlineDataSourceName = getParamField(item, 'dataSourceName');
    const tooltipName = getParamField(item, 'dbTooltipName');

    const iconHtml = (() => {
      if (lineSourceIcon) return getLineSourceIcon(lineSourceIcon);
      if (inlineDataSourceKey) {
        const icon = getLineSourceIcon(inlineDataSourceKey, true);
        if (icon) return icon;
      }
      return getListLeftImg(item);
    })();

    const nameHtml = (() => {
      if (tooltipName) return getLineSourceName(tooltipName);
      if (lineSourceName && lineSourceName.trim() !== '') return getLineSourceName(lineSourceName);
      if (inlineDataSourceName) return getLineSourceName(inlineDataSourceName);
      return getListLeftText(item);
    })();

    return `${iconHtml}
        ${imageType != 'region' ? getListLeftGameImg(item) : ''}
        ${nameHtml}`;
  };

  const getListImg = (item: any) => {
    // console.log('barListValuebarListValue', barListValue);
    // console.log('itemitem', item);
    switch (topChartsType) {
      case 'newrelease':
      case 'upcoming':
        return getIcon(titleNow);
      // case 'upcoming':
      //   return null;
      case 'rpd':
        return getIcon(dataSource);
      default: {
        const dataSourceKey = getParamField(item, 'dataSourceKey');
        if (dataSourceKey) {
          const icon = getIcon(dataSourceKey);
          if (icon) return icon;
        }
        return getIcon(item?.seriesName);
      }
      // (Array.isArray(data) && data?.length > 0 && data[0]?.seriesName) ? data[0]?.seriesName : '';
    }
  };

  const getListLeftBarContent = (item: any) => {
    // let img = item?.data?.market || '';
    switch (topChartsType) {
      case TOP_CHARTS_TYPES.COMPARE_PC_CONSOLE_LINE:
      case TOP_CHARTS_TYPES.STORE_RANK_MORE_BAR:
      case TOP_CHARTS_TYPES.STORE_RANK_DETAILS:
      case 'steamSales—players_by_country-topcharts':
      case 'pcconsole-country-bar':
      case 'pcconsole-country-ampere-bar':
        return '';
      case 'newrelease':
      case 'upcoming':
        return `<div class="bubble-lf-imgbox">${renderTooltipImage(
          getListImg(item),
          `bubble-lf-img ${imgInvertChartBubble.includes(titleNow) ? 'db-invert-unset' : ''}`,
        )}</div>`;
      case 'global-view-genre-market':
        const cover = item?.data?.cover;
        return cover && cover != ''
          ? renderTooltipImage(cover, 'dbi-tooltip-body-item-left-img')
          : '';
      case 'steamSales—countries-players_by_country':
        const regionCode = item?.data?.cover || item?.data?.url || item?.data?.market || '';
        return `<div class="bubble-lf-gameimgbox-country">${renderTooltipImage(
          getCountryFlag((regionCode == 'Others' ? '' : regionCode)?.toLocaleUpperCase()),
          'bubble-title-flex-img-country-fix',
        )}</div>`;
      default:
        return !ifNeedSourceImg || item?.seriesName == '' || isStackBar
          ? ''
          : `<div class="bubble-lf-imgbox">
        ${renderTooltipImage(getListImg(item), 'bubble-lf-img')}
      </div>`;
    }
  };

  const getMarkerColor = (item: any) => {
    if (!isTrueDataItem(item)) return item?.color;
    // 新真数 series 可显式声明 tooltip 颜色；旧散点未声明时保持原有灰色。
    return item?.data?.dbTrueDataColor || '#999999';
  };

  const getTooltipMarkerHtml = (item: any) => {
    const markerClassName = isTrueDataItem(item)
      ? 'bubble-lf-round bubble-lf-round--diamond'
      : 'bubble-lf-round';
    return `<div class="${markerClassName}" style="background-color: ${getMarkerColor(
      item,
    )};display:inline-block"></div>`;
  };

  const getListLeftContent = (item: any) => {
    // console.log('item~!!!!!!!!!!!!!!!', item)
    if (isBar()) {
      return `${getListLeftBarContent(item)}<div class="bubble-lf-name ${getListName(item) == '' ? 'none-content' : ''
        }">${getListName(item)}</div>`;
    }
    if (isLine()) {
      return getListLeftLineContent(item);
    }
    if (isPie()) {
      return `<div class="bubble-lf-name">${item.name || ''}</div>`;
    }
  };

  const getTitleHeadContent = (data: any) => {
    const titleStr = topTitle ? topTitle : getTitleValue(data);
    return isBar() && titleStr != ''
      ? `<div class="dbi-chart-bubble-title">
              <div class="bubble-title-flex">
                ${getTitleImg(data)}
                <div class="bubble-title-flex-text">${titleStr}</div>
              </div>
            </div >`
      : '';
  };

  const getTotalNum = (data: any) => {
    let _totalNum = 0;
    if (Array.isArray(data) && data?.length > 0) {
      for (const i in data) {
        const value =
          Array.isArray(data[i]?.value) && data[i]?.value?.length > 1
            ? data[i].value[1]
            : data[i]?.value || 0;
        _totalNum = _totalNum + value * 1;
      }
    }
    return _totalNum;
  };

  const ifHasOtherContent = (data: any) => {
    const otherContent = {
      leftContent: '',
      rightContent: '',
    };

    switch (topChartsType) {
      case 'details-streaming':
        otherContent.leftContent = `Sum`;
        otherContent.rightContent = `${newMathFormat(getTotalNum(data))}`;
        break;
      case 'pcconsole-country-bar':
      case 'pcconsole-country-ampere-bar':
        otherContent.leftContent = ifHasOtherHeadContent || isCenter ? `All` : '';
        otherContent.rightContent =
          ifHasOtherHeadContent || isCenter
            ? `${newMathFormat(getTotalNum(isCenter ? centerList : data))}`
            : '';
        break;
      case 'combine-stats-line':
      case 'combine-stats-pie':
      case 'pcconsole-stats-line-country': {
        // 按平台饼图 hover 单个扇形时，data 为单个 ECharts 项而非数组，All 行需用 centerList 汇总
        const dataList =
          topChartsType === 'combine-stats-pie' && ifHasOtherHeadContent
            ? centerList
            : isCenter
              ? centerList
              : data;
        otherContent.leftContent = ifHasOtherHeadContent ? `All` : '';
        otherContent.rightContent = ifHasOtherHeadContent
          ? `${newMathFormat(getTotalNum(dataList))}`
          : '';
        break;
      }
      case 'overview-card':
        otherContent.leftContent = `${leftContent}`;
        otherContent.rightContent = `${rightContent}`;
        break;
      case 'marketProfile-map':
        otherContent.leftContent = `${leftContent}</div>
                                    <div class="dbi-chart-bubble-lf-new-child">${headerLeftTitle}`;
        otherContent.rightContent = `${formatEnNumber(data.data?.gameCount)}</div>
                                    <div class="dbi-chart-bubble-rt-new-child">${formatEnNumber(
          data.data?.value,
        )}`;
        break;
      case 'marketProfile-mapTree':
        otherContent.leftContent = `${leftContent || data?.name}`;
        otherContent.rightContent = `${rightContent}`;
        break;
    }
    return otherContent;
  };

  const newDataList = (list: any) => {
    const newArr: any = [];
    if (Array.isArray(list)) {
      list?.forEach((item: any, index: any) => {
        newArr.push(item);
      });
    }
    return newArr;
  };

  const getContex = () => {
    let contex = `<div class="dbi-chart-bubble-item dbi-chart-bubble-item-new">`;
    let contextLeft = `<div class="dbi-chart-bubble-lf dbi-chart-bubble-lf-new">`;
    let contextRight = `<div class="dbi-chart-bubble-rt dbi-chart-bubble-rt-new">`;

    const hasLeftSecond =
      secondTitleLeft === ' '
        ? false
        : (secondTitleLeft && secondTitleLeft != '') || getLeftTimeText(data) != '';
    const hasRightSecond =
      secondTitleRight === ' '
        ? false
        : (secondTitleRight && secondTitleRight != '') || getRightTitle(data) != '';

    if (hasLeftSecond) {
      contextLeft += `<div class="bubble-title-flex-text second-title-left ${imageType === 'compare' ? 'second-title-left-compare' : ''
        }">${secondTitleLeft ? secondTitleLeft : getLeftTimeText(data)}</div>`;
    }
    if (hasRightSecond) {
      contextRight += `<div class="bubble-title-text second-title-right ${imageType === 'compare' ? 'second-title-left-compare' : ''
        }">${secondTitleRight ? secondTitleRight : getRightTitle(data)}</div>`;
    }
    // 两对称内容，缺少即补上占位dom
    // 有左二，无右二，补右二
    if (hasLeftSecond && !hasRightSecond) {
      contextRight += `<div class="bubble-title-text second-title-right none-content"> </div>`;
    }
    // 同理补左二
    if (hasRightSecond && !hasLeftSecond) {
      contextLeft += `<div class="bubble-title-flex-text second-title-left none-content"> </div>`;
    }

    if ((otherTitleLeft && otherTitleLeft != '') || ifHasOtherContent(data)?.leftContent != '') {
      contextLeft += `<div class="dbi-chart-bubble-lf-new-child">${otherTitleLeft ? otherTitleLeft : ifHasOtherContent(data)?.leftContent
        }</div>`;
    }
    if ((otherTitleRight && otherTitleRight != '') || ifHasOtherContent(data)?.rightContent != '') {
      contextRight += `<div class="dbi-chart-bubble-rt-new-child">${otherTitleRight ? otherTitleRight : ifHasOtherContent(data)?.rightContent
        }</div>`;
    }

    if ((data && Array.isArray(data)) || isCenter) {
      const dataList = isCenter ? newDataList(centerList) : data;
      const hasTrueData = Array.isArray(dataList)
        ? dataList.some((it: any) => isTrueDataItem(it))
        : false;
      let iterList: any[] = Array.isArray(dataList) ? [...dataList] : [];
      if (SORTABLE_LINE_TOPCHARTSTYPES.includes(topChartsType) || isCenter) {
        const sortType = SORT_TYPES_LINE_TOPCHARTSTYPES?.[topChartsType] || 'desc';
        // If tooltip contains True Data rows, sort by parent KPI value and keep True Data rows under their parent
        if (hasTrueData) {
          const parents = iterList.filter((x: any) => !isTrueDataItem(x));
          const trues = iterList.filter((x: any) => isTrueDataItem(x));
          // Build mapping from parent name to its True Data children
          const map = new Map<string, any[]>();
          trues.forEach((t: any) => {
            const key = t?.data?.__parentName || '';
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(t);
          });
          // Parent value extraction
          const getNum = (x: any) => {
            let v = Array.isArray(x?.value) && x?.value?.length > 1 ? x?.value[1] : x?.value;
            if (topChartsType === TOP_CHARTS_TYPES.STREAMSTRING_INFLUENCER) {
              v = x?.value?.slice(-1)?.[0];
            }
            const n = Number(v);
            const invalid = v == '' ? true : Number.isNaN(n);
            return { n, invalid };
          };
          parents.sort((a: any, b: any) => {
            const A = getNum(a);
            const B = getNum(b);
            if (A.invalid && B.invalid) return 0;
            if (A.invalid) return 1;
            if (B.invalid) return -1;
            return sortType === 'asc' ? A.n - B.n : B.n - A.n;
          });
          const merged: any[] = [];
          const appended = new Set<any>();
          parents.forEach((p: any) => {
            merged.push(p);
            appended.add(p);
            const kids = map.get(p?.seriesName) || [];
            kids.forEach((k) => {
              merged.push(k);
              appended.add(k);
            });
          });
          // Append any True Data rows without a matching parent line
          trues.forEach((t) => {
            if (!appended.has(t)) merged.push(t);
          });
          iterList = merged;
        } else {
          // Original sorting behavior when there's no True Data
          // 此处排序，需要把iterList里面value为''或null的排除放最后，剩下的进行排序，优化此处
          iterList.sort((a: any, b: any) => {
            let cloneValueA =
              Array.isArray(a?.value) && a?.value?.length > 1 ? a?.value[1] : a?.value;
            let cloneValueB =
              Array.isArray(b?.value) && b?.value?.length > 1 ? b?.value[1] : b?.value;

            if (topChartsType === TOP_CHARTS_TYPES.STREAMSTRING_INFLUENCER) {
              cloneValueA = a?.value.slice(-1)[0];
              cloneValueB = b?.value.slice(-1)[0];
            }
            const aValue = Number(cloneValueA);
            const bValue = Number(cloneValueB);
            const isAvalueInvalid = !cloneValueA ? true : Number.isNaN(aValue);
            const isBvalueInvalid = !cloneValueB ? true : Number.isNaN(bValue);
            if (isAvalueInvalid && isBvalueInvalid) return 0;
            if (isAvalueInvalid) return 1;
            if (isBvalueInvalid) return -1;
            return sortType === 'asc' ? aValue - bValue : bValue - aValue;
          });
        }
      }
      // 按国家折线图/饼图：排序后将 Others 强制移到列表第一位
      if (
        topChartsType === 'combine-stats-line' ||
        topChartsType === 'combine-stats-pie' ||
        topChartsType === 'pcconsole-stats-line-country'
      ) {
        const othersIdx = iterList.findIndex(
          (item: any) => (item?.seriesName || item?.name || '').toLowerCase() === 'others',
        );
        if (othersIdx > 0) {
          const [othersItem] = iterList.splice(othersIdx, 1);
          iterList.unshift(othersItem);
        }
      }
      iterList = moveTooltipOthersLast(iterList);
      // 这里样式是： 2列中间距离（左边最长的与右边最长的距离)至少有30px
      iterList?.forEach((item: any) => {
        const leftChildCls = `dbi-chart-bubble-lf-new-child`;
        contextLeft += `<div class="${leftChildCls}">${getTooltipMarkerHtml(item)}
         ${getListLeftContent(item)}</div>`;
        contextRight += `<div class="dbi-chart-bubble-rt-new-child">${getListValue(item)}</div>`;
      });
    } else if (data?.data) {
      if (ifHasListContent()) {
        const leftChildCls = `dbi-chart-bubble-lf-new-child`;
        contextLeft += `<div class="${leftChildCls}">${getTooltipMarkerHtml(data)}
        ${getListLeftContent(data)}</div>`;
        contextRight += `<div class="dbi-chart-bubble-rt-new-child"> ${getListValue(data)}</div>`;
      }
    }
    contex += `${contextLeft}</div> <div style="width:30px;"></div>${contextRight}</div> </div>`;
    return contex;
  };

  return `<div class="dbi-chart-bubble ${topChartsType == 'overview-card' ? 'nomin' : ''} ">
            <div class="dbi-chart-bubble-content">
              ${getTitleHeadContent(data)}
              ${getContex()}
            </div>
          </div> `;
};

export default ChartBubble;
